import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Popconfirm, Popover, Skeleton } from 'antd';

import { formatRelativeToNow } from '../../utils/formatters';
import { normalizeId } from '../../utils/ids';
import { destructivePopconfirmProps } from '../common/confirmDestructive';

// Five is what fits on one line of a desktop form; the rest wait behind
// "More…" rather than wrapping the form's first field off the screen.
export const VISIBLE_DRAFTS = 5;

// The popover names this many fields, then "and N more".
export const PREVIEW_FIELDS = 6;

/** "Draft · 2 hours ago · Pump 3" — the chip's words, and its accessible name. */
export function draftChipLabel(draft) {
	const age = formatRelativeToNow(draft?.updatedAt);
	const title = String(draft?.title || '').trim() || 'Untitled';
	return ['Draft', age, title].filter(Boolean).join(' · ');
}

/**
 * What one draft holds, in its chip's popover: the fields it sets and their
 * values (cut to a line each), when it was saved, and what can be done with
 * it — Apply (primary) and Delete (red, confirmed). The values are fetched
 * when the popover opens (the list carries none: SCHEMA.md § Item drafts);
 * `describe(draft)` turns them into `[{ key, label, text }]` rows, since only
 * the form knows the card's elements.
 */
function DraftPreview({ row, state, describe, onApply, onDelete, onClose }) {
	const applyRef = useRef(null);
	const title = String(row?.title || '').trim() || 'Untitled draft';
	const age = formatRelativeToNow(row?.updatedAt);
	const ready = state?.status === 'ready';
	const fields = ready ? describe(state.draft) : [];
	const shown = fields.slice(0, PREVIEW_FIELDS);
	const more = fields.length - shown.length;

	// Keyboard: the popover's first stop is Apply, once there is something
	// to apply — Tab from the chip would otherwise skip the portal entirely.
	useEffect(() => {
		if (ready) applyRef.current?.focus?.();
	}, [ready]);

	const onKeyDown = (event) => {
		if (event.key !== 'Escape') return;
		// Closes the popover, not the form beneath it.
		event.stopPropagation();
		event.preventDefault();
		onClose(true);
	};

	return (
		<div className="draft-preview" role="dialog" aria-label={`Draft: ${title}`} onKeyDown={onKeyDown}>
			<div className="draft-preview__head">
				<span className="draft-preview__title">{title}</span>
				{age ? <span className="draft-preview__meta">Saved {age}</span> : null}
			</div>
			{state?.status === 'error' ? (
				<p className="draft-preview__empty">That draft is no longer available.</p>
			) : !ready ? (
				<Skeleton active title={false} paragraph={{ rows: 2 }} className="draft-preview__loading" />
			) : shown.length ? (
				<>
					<dl className="draft-preview__fields">
						{shown.map(field => (
							<div key={field.key} className="draft-preview__field">
								<dt>{field.label}</dt>
								<dd title={field.text}>{field.text}</dd>
							</div>
						))}
					</dl>
					{more > 0 ? (
						<p className="draft-preview__more">and {more} more {more === 1 ? 'field' : 'fields'}</p>
					) : null}
				</>
			) : (
				<p className="draft-preview__empty">Nothing filled in yet.</p>
			)}
			<div className="draft-preview__actions">
				<Button ref={applyRef} type="primary" disabled={!ready} onClick={() => onApply(state.draft)}>
					Apply
				</Button>
				<Popconfirm
					title="Delete this draft?"
					{...destructivePopconfirmProps('Delete')}
					onConfirm={onDelete}
				>
					<Button danger>Delete</Button>
				</Popconfirm>
			</div>
		</div>
	);
}

/**
 * The drafts a new-item form can pick up (keepr-api SCHEMA.md § Item
 * drafts): this person's drafts of this card in this collection, newest
 * first, as chips above the form. A new form always opens BLANK — the
 * owner's rule is "don't force the user to take their last draft".
 *
 * A chip opens a popover showing what the draft holds, with Apply and
 * Delete (the owner's live pass: "the draft chip should have a popover to
 * show what it contains"). Apply is the only way a draft lands on the form.
 *
 *   drafts      the list rows (no values)
 *   loadDraft   id → Promise of the draft with its values
 *   describe    draft → [{ key, label, text }], the fields it sets
 *   onApply     (id, draft) — the popover already fetched it
 *   onDelete    id → Promise; the chip goes when it resolves
 *   busyId      the chip whose draft is being applied
 *
 * Renders nothing when there are none.
 */
export default function DraftChips({ drafts, loadDraft, describe, onApply, onDelete, busyId = null }) {
	const [expanded, setExpanded] = useState(false);
	const [openId, setOpenId] = useState(null);
	const [states, setStates] = useState({});
	const chipRefs = useRef(new Map());
	const openIdRef = useRef(null);
	openIdRef.current = openId;
	const list = Array.isArray(drafts) ? drafts : [];

	const open = useCallback((id) => {
		setOpenId(id);
		setStates(prev => ({ ...prev, [id]: { status: 'loading' } }));
		Promise.resolve()
			.then(() => loadDraft(id))
			.then(draft => setStates(prev => ({ ...prev, [id]: draft ? { status: 'ready', draft } : { status: 'error' } })))
			.catch(() => setStates(prev => ({ ...prev, [id]: { status: 'error' } })));
	}, [loadDraft]);

	const close = useCallback((refocus = false) => {
		const current = openIdRef.current;
		setOpenId(null);
		if (refocus && current) chipRefs.current.get(current)?.focus?.();
	}, []);

	// A chip that left the list (applied, deleted) takes its popover with it.
	const ids = list.map(row => normalizeId(row?._id)).join('|');
	useEffect(() => {
		if (openId && !ids.split('|').includes(openId)) setOpenId(null);
	}, [ids, openId]);

	if (!list.length) return null;
	const shown = expanded ? list : list.slice(0, VISIBLE_DRAFTS);
	const hidden = list.length - shown.length;

	return (
		<div className="item-form-drafts" role="group" aria-label="Your drafts of this card" data-testid="draft-chips">
			<span className="item-form-drafts__label">Pick up where you left off</span>
			<div className="item-form-drafts__chips">
				{shown.map(draft => {
					const id = normalizeId(draft?._id);
					const label = draftChipLabel(draft);
					const isOpen = openId === id;
					return (
						<Popover
							key={id}
							trigger="click"
							placement="bottomLeft"
							open={isOpen}
							onOpenChange={next => (next ? open(id) : close())}
							destroyOnHidden
							rootClassName="draft-preview-popover"
							content={(
								<DraftPreview
									row={draft}
									state={states[id]}
									describe={describe}
									onClose={close}
									onApply={(full) => { close(); onApply?.(id, full); }}
									onDelete={() => Promise.resolve(onDelete?.(id)).then(() => close())}
								/>
							)}
						>
							<Button
								ref={node => { if (node) chipRefs.current.set(id, node); else chipRefs.current.delete(id); }}
								size="small"
								className="item-form-drafts__chip"
								loading={busyId === id}
								aria-haspopup="dialog"
								aria-expanded={isOpen}
								title={label}
							>
								<span className="item-form-drafts__chip-text">{label}</span>
							</Button>
						</Popover>
					);
				})}
				{hidden > 0 ? (
					<Button size="small" type="link" className="item-form-drafts__more" onClick={() => setExpanded(true)}>
						More…
					</Button>
				) : null}
			</div>
		</div>
	);
}
